/**
 * StackRankingPublicVote - Public voting interface for external participants
 * Features: Persistent guest sessions, full-screen layout, SR branding
 * Logged-in users vote as themselves, guests can vote or login
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, BarChart3, CheckCircle, ChevronUp, ChevronDown,
  Send, Plus, X, User, LogIn
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { stackRankingApi } from '../services/api';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// Method labels
const METHOD_LABELS = {
  drag_drop: 'Drag & Drop',
  pairwise: 'Pairwise Comparison',
  dot_voting: 'Dot Voting',
  points_allocation: 'Points Allocation',
};

// Guest session storage key
const GUEST_SESSION_KEY = 'sr_guest_session';

// Per-space vote tracking key
const getVotedKey = (shareToken) => `sr_voted_${shareToken}`;

// Check if guest has voted in this space (via localStorage)
const hasGuestVoted = (shareToken) => {
  return localStorage.getItem(getVotedKey(shareToken)) === 'true';
};

// Mark guest as having voted in this space
const markGuestVoted = (shareToken) => {
  localStorage.setItem(getVotedKey(shareToken), 'true');
};

// Clear guest vote flag (for testing/admin purposes)
const clearGuestVoted = (shareToken) => {
  localStorage.removeItem(getVotedKey(shareToken));
};

// Get or create guest session (persists for 30 days)
const getGuestSession = () => {
  const stored = localStorage.getItem(GUEST_SESSION_KEY);
  if (stored) {
    try {
      const session = JSON.parse(stored);
      // Check if session is still valid (30 days)
      if (session.expiresAt > Date.now()) {
        return session;
      }
    } catch (e) {
      console.error('Failed to parse guest session:', e);
    }
  }
  return null;
};

// Save guest session
const saveGuestSession = (name, email, segment) => {
  const session = {
    name,
    email,
    segment,
    expiresAt: Date.now() + (30 * 24 * 60 * 60 * 1000), // 30 days
  };
  localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(session));
  return session;
};

const StackRankingPublicVote = () => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [space, setSpace] = useState(null);
  const [items, setItems] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [alreadyVoted, setAlreadyVoted] = useState(false);  // Track if user already voted
  
  // Participant info - initialize from stored session or logged-in user
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [participantName, setParticipantName] = useState('');
  const [participantEmail, setParticipantEmail] = useState('');
  const [participantSegment, setParticipantSegment] = useState('');
  const [hasStoredSession, setHasStoredSession] = useState(false);
  const [isGuest, setIsGuest] = useState(null);  // null = show choice, true = guest form, false = logged in
  const [showLoginChoice, setShowLoginChoice] = useState(false);  // Whether to show login/guest choice
  
  // Voting state
  const [votes, setVotes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // For drag and drop
  const [rankedItems, setRankedItems] = useState([]);
  
  // For pairwise
  const [currentPair, setCurrentPair] = useState(null);
  const [pairIndex, setPairIndex] = useState(0);
  const [pairs, setPairs] = useState([]);
  
  // For dot voting
  const [dotAllocations, setDotAllocations] = useState({});
  
  // For points allocation
  const [pointAllocations, setPointAllocations] = useState({});
  
  // Suggestion
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [suggestionTitle, setSuggestionTitle] = useState('');
  const [suggestionDescription, setSuggestionDescription] = useState('');
  const [suggesting, setSuggesting] = useState(false);

  // Load space, items, and check for stored session
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load space and items using new voting endpoints
        const [spaceRes, itemsRes] = await Promise.all([
          stackRankingApi.getVotingSpace(shareToken),
          stackRankingApi.getVotingItems(shareToken),
        ]);
        setSpace(spaceRes.data);
        setItems(itemsRes.data);
        setRankedItems(itemsRes.data);
        
        // Check if the response includes authenticated user info
        const authenticatedUser = spaceRes.data.authenticated_user;
        
        // Check if user is already authenticated - they vote as themselves
        if (authenticatedUser || (isAuthenticated && user)) {
          const userName = authenticatedUser?.name || user?.name || user?.email;
          const userEmail = authenticatedUser?.email || user?.email;
          
          setParticipantName(userName);
          setParticipantEmail(userEmail);
          setIsGuest(false);
          setHasStoredSession(true);  // Skip the modal entirely
          setShowParticipantModal(false);  // Ensure modal is closed
          setShowLoginChoice(false);  // No need for login choice
          
          // Check if already voted using the new status endpoint
          try {
            const voteCheck = await stackRankingApi.getVoteStatus(shareToken);
            if (voteCheck.data.has_voted) {
              setAlreadyVoted(true);
              setLoading(false);
              return; // Show already voted screen
            }
          } catch (e) {
            console.log('Vote status check failed:', e.response?.status);
          }
        } else if (spaceRes.data.access_type === 'team' || spaceRes.data.access_type === 'team_only') {
          // For team-only spaces without auth, they'll see the login_required error
          // This case shouldn't normally happen since the API would reject unauthenticated requests
          setShowLoginChoice(false);
          setShowParticipantModal(false);
        } else {
          // Check for stored guest session and local vote flag
          const storedSession = getGuestSession();
          
          // Check localStorage for existing vote on this space
          if (hasGuestVoted(shareToken)) {
            setAlreadyVoted(true);
            if (storedSession) {
              setParticipantName(storedSession.name);
              setHasStoredSession(true);
              setIsGuest(true);
            }
          } else if (storedSession) {
            setParticipantName(storedSession.name);
            setParticipantEmail(storedSession.email || '');
            setParticipantSegment(storedSession.segment || '');
            setHasStoredSession(true);
            setIsGuest(true);
          } else {
            // Show the login/guest choice first
            setShowLoginChoice(true);
            setShowParticipantModal(true);
          }
        }
        
        // Initialize pairwise
        if (spaceRes.data.ranking_method === 'pairwise' && itemsRes.data.length >= 2) {
          const allPairs = [];
          for (let i = 0; i < itemsRes.data.length; i++) {
            for (let j = i + 1; j < itemsRes.data.length; j++) {
              allPairs.push([itemsRes.data[i], itemsRes.data[j]]);
            }
          }
          const shuffled = allPairs.sort(() => Math.random() - 0.5);
          const numPairs = Math.min(shuffled.length, Math.ceil(itemsRes.data.length * 1.5));
          setPairs(shuffled.slice(0, numPairs));
          setCurrentPair(shuffled[0]);
        }
      } catch (err) {
        console.error('Failed to load space:', err);
        const errorDetail = err.response?.data?.detail || 'This ranking is not available';
        const statusCode = err.response?.status;
        
        // Check if it's a team-only space requiring authentication
        if (statusCode === 401 && errorDetail.includes('authentication')) {
          setError('login_required');
        } else if (statusCode === 403) {
          setError('no_access');
        } else {
          setError(errorDetail);
        }
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [shareToken, isAuthenticated, user]);

  const dotsRemaining = space ? (space.dots_per_person || 5) - Object.values(dotAllocations).reduce((a, b) => a + b, 0) : 0;
  const pointsRemaining = space ? (space.max_points || 100) - Object.values(pointAllocations).reduce((a, b) => a + b, 0) : 0;

  // Handle login redirect
  const handleLogin = () => {
    // Store the return URL so user comes back after login
    sessionStorage.setItem('loginReturnUrl', window.location.pathname);
    navigate('/login');
  };

  // Continue as guest
  const handleContinueAsGuest = () => {
    setIsGuest(true);
    setShowLoginChoice(false);
  };

  // Start voting after entering name - save session for future visits
  const handleStartVoting = () => {
    if (!participantName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    // Save session for future visits
    saveGuestSession(participantName.trim(), participantEmail.trim(), participantSegment.trim());
    setShowParticipantModal(false);
  };

  // Handle pairwise vote
  const handlePairwiseVote = (winner, loser) => {
    const newVotes = [...votes, { winner_id: winner.item_id, loser_id: loser.item_id }];
    setVotes(newVotes);
    
    if (pairIndex + 1 < pairs.length) {
      setPairIndex(pairIndex + 1);
      setCurrentPair(pairs[pairIndex + 1]);
    } else {
      submitVotes(newVotes);
    }
  };

  // Handle dot allocation
  const allocateDot = (itemId, delta) => {
    const current = dotAllocations[itemId] || 0;
    const newValue = Math.max(0, current + delta);
    if (delta > 0 && dotsRemaining <= 0) return;
    setDotAllocations(prev => ({ ...prev, [itemId]: newValue }));
  };

  // Handle points allocation
  const allocatePoints = (itemId, value) => {
    const numValue = parseInt(value) || 0;
    const current = pointAllocations[itemId] || 0;
    const maxAllowed = pointsRemaining + current;
    const finalValue = Math.min(Math.max(0, numValue), maxAllowed);
    setPointAllocations(prev => ({ ...prev, [itemId]: finalValue }));
  };

  // Move item in drag and drop
  const moveItem = (index, direction) => {
    const newItems = [...rankedItems];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newItems.length) return;
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    setRankedItems(newItems);
  };

  // Submit votes
  const submitVotes = async (votesToSubmit = votes) => {
    setSubmitting(true);
    try {
      let voteData = [];
      
      if (space.ranking_method === 'drag_drop') {
        voteData = rankedItems.map((item, idx) => ({
          item_id: item.item_id,
          rank: idx + 1,
        }));
      } else if (space.ranking_method === 'pairwise') {
        voteData = votesToSubmit;
      } else if (space.ranking_method === 'dot_voting') {
        voteData = Object.entries(dotAllocations)
          .filter(([_, dots]) => dots > 0)
          .map(([item_id, dots]) => ({ item_id, dots }));
      } else if (space.ranking_method === 'points_allocation') {
        voteData = Object.entries(pointAllocations)
          .filter(([_, points]) => points > 0)
          .map(([item_id, points]) => ({ item_id, points }));
      }
      
      // Use the new clean voting endpoint
      await stackRankingApi.submitVote(shareToken, { 
        votes: voteData, 
        participant: {
          name: participantName,
          email: participantEmail || null,
          segment: participantSegment || null,
        }
      });
      
      // Mark as voted in localStorage for guests
      if (isGuest) {
        markGuestVoted(shareToken);
      }
      
      setCompleted(true);
      toast.success('Thank you for voting!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to submit vote';
      if (errorMsg.includes('already voted')) {
        setAlreadyVoted(true);
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Submit suggestion
  const handleSuggest = async () => {
    if (!suggestionTitle.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    setSuggesting(true);
    try {
      await stackRankingApi.suggestItem(
        shareToken,
        suggestionTitle.trim(),
        suggestionDescription.trim(),
        participantName
      );
      toast.success('Suggestion submitted!');
      setSuggestionTitle('');
      setSuggestionDescription('');
      setShowSuggestModal(false);
    } catch (err) {
      toast.error('Failed to submit suggestion');
    } finally {
      setSuggesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    // Special handling for login required errors
    if (error === 'login_required') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
          <Card className="p-8 text-center max-w-md">
            <LogIn className="w-16 h-16 mx-auto mb-4 text-blue-500" />
            <h2 className="text-xl font-semibold mb-2">Login Required</h2>
            <p className="text-gray-600 mb-6">
              This is a team-only ranking. Please login with your team account to vote.
            </p>
            <Button onClick={handleLogin} className="w-full">
              <LogIn className="w-4 h-4 mr-2" />
              Login to Vote
            </Button>
          </Card>
        </div>
      );
    }
    
    // No access error
    if (error === 'no_access') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
          <Card className="p-8 text-center max-w-md">
            <BarChart3 className="w-16 h-16 mx-auto mb-4 text-orange-400" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-gray-600">
              You don&apos;t have access to this team-only ranking.
            </p>
          </Card>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-semibold mb-2">Not Available</h2>
          <p className="text-gray-600">{error}</p>
        </Card>
      </div>
    );
  }

  // Show "already voted" screen
  if (alreadyVoted && !completed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-blue-500" />
          <h2 className="text-xl font-semibold mb-2">Already Voted</h2>
          <p className="text-gray-600 mb-4">
            {participantName ? `Hi ${participantName}! ` : ''}You have already voted in this ranking.
          </p>
          <p className="text-sm text-gray-500">
            Each person can only vote once to ensure fair results.
          </p>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
          <h2 className="text-xl font-semibold mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-4">Your vote has been recorded.</p>
          {space.allow_suggestions && (
            <Button variant="outline" onClick={() => setShowSuggestModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Suggest an Item
            </Button>
          )}
        </Card>
        
        {/* Suggestion Modal */}
        <Dialog open={showSuggestModal} onOpenChange={setShowSuggestModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Suggest an Item</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium">Title *</label>
                <Input
                  placeholder="What should be added?"
                  value={suggestionTitle}
                  onChange={(e) => setSuggestionTitle(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  placeholder="Optional details"
                  value={suggestionDescription}
                  onChange={(e) => setSuggestionDescription(e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setShowSuggestModal(false)}>Cancel</Button>
              <Button onClick={handleSuggest} disabled={suggesting}>
                {suggesting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Participant Info Modal */}
      <Dialog open={showParticipantModal} onOpenChange={() => {}}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Welcome!</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <p className="text-gray-600 mb-4">
              You&apos;re about to participate in: <strong>{space?.name}</strong>
            </p>
            
            {/* Show login/guest choice first for anyone_with_link access */}
            {showLoginChoice ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-500 text-center">How would you like to participate?</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    onClick={handleLogin}
                    className="h-20 flex-col gap-2"
                  >
                    <LogIn className="w-6 h-6" />
                    <span>Login to Vote</span>
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleContinueAsGuest}
                    className="h-20 flex-col gap-2"
                  >
                    <User className="w-6 h-6" />
                    <span>Vote as Guest</span>
                  </Button>
                </div>
                <p className="text-xs text-gray-500 text-center">
                  Login to vote with your account, or continue as guest
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Your Name *</label>
                  <Input
                    placeholder="Enter your name"
                    value={participantName}
                    onChange={(e) => setParticipantName(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Email (optional)</label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={participantEmail}
                    onChange={(e) => setParticipantEmail(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Role/Team (optional)</label>
                  <Input
                    placeholder="e.g., Engineering, Customer"
                    value={participantSegment}
                    onChange={(e) => setParticipantSegment(e.target.value)}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Used for segmenting results</p>
                </div>
              </div>
            )}
          </div>
          {!showLoginChoice && (
            <DialogFooter className="mt-6">
              <Button onClick={handleStartVoting} className="w-full">
                Start Voting
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Header with SR Logo */}
      <div className="bg-white border-b px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          {/* SR Logo */}
          <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
            SR
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-semibold">{space?.name}</h1>
            <p className="text-xs text-gray-500">
              {METHOD_LABELS[space?.ranking_method]} • {items.length} items
            </p>
          </div>
          {hasStoredSession && (
            <div className="text-sm text-gray-500">
              Voting as <span className="font-medium">{participantName}</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Voting Content - Full height */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto px-6 py-8">
        {!showParticipantModal && (
          <>
            {/* Drag and Drop */}
            {space.ranking_method === 'drag_drop' && (
              <div className="space-y-4">
                <p className="text-gray-600">
                  Arrange items from most important (top) to least important (bottom)
                </p>
                <div className="space-y-2">
                  {rankedItems.map((item, index) => (
                    <Card key={item.item_id} className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="flex flex-col gap-1">
                          <Button variant="ghost" size="sm" onClick={() => moveItem(index, -1)} disabled={index === 0}>
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => moveItem(index, 1)} disabled={index === rankedItems.length - 1}>
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center font-medium">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">{item.title}</h4>
                          {item.description && <p className="text-sm text-gray-500">{item.description}</p>}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
                <Button onClick={() => submitVotes()} disabled={submitting} className="w-full">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Submit Ranking
                </Button>
              </div>
            )}

            {/* Pairwise */}
            {space.ranking_method === 'pairwise' && currentPair && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <p className="text-sm text-gray-500">Question {pairIndex + 1} of {pairs.length}</p>
                  <h3 className="text-2xl font-semibold mt-2">Which is more important?</h3>
                </div>
                <div className="grid grid-cols-2 gap-6 mb-8">
                  {currentPair.map((item) => (
                    <Card
                      key={item.item_id}
                      onClick={() => handlePairwiseVote(item, currentPair.find(i => i.item_id !== item.item_id))}
                      className="p-8 cursor-pointer transition-all hover:shadow-xl hover:border-blue-500 hover:scale-[1.02] min-h-[200px] flex flex-col justify-center"
                    >
                      <h4 className="font-semibold text-xl text-center mb-3">{item.title}</h4>
                      {item.description && <p className="text-sm text-center text-gray-500">{item.description}</p>}
                    </Card>
                  ))}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${((pairIndex + 1) / pairs.length) * 100}%` }} />
                </div>
              </div>
            )}

            {/* Dot Voting */}
            {space.ranking_method === 'dot_voting' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-gray-600">Allocate your dots to the most important items</p>
                  <Badge variant="secondary">{dotsRemaining} dots left</Badge>
                </div>
                <div className="space-y-2">
                  {items.map((item) => (
                    <Card key={item.item_id} className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => allocateDot(item.item_id, -1)} disabled={(dotAllocations[item.item_id] || 0) === 0}>-</Button>
                          <span className="w-8 text-center font-medium">{dotAllocations[item.item_id] || 0}</span>
                          <Button variant="outline" size="sm" onClick={() => allocateDot(item.item_id, 1)} disabled={dotsRemaining === 0}>+</Button>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">{item.title}</h4>
                          {item.description && <p className="text-sm text-gray-500">{item.description}</p>}
                        </div>
                        <div className="flex gap-1">
                          {Array.from({ length: dotAllocations[item.item_id] || 0 }).map((_, i) => (
                            <div key={i} className="w-3 h-3 rounded-full bg-blue-500" />
                          ))}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
                <Button onClick={() => submitVotes()} disabled={submitting || dotsRemaining === (space.dots_per_person || 5)} className="w-full">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Submit Votes
                </Button>
              </div>
            )}

            {/* Points Allocation */}
            {space.ranking_method === 'points_allocation' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-gray-600">Distribute your points across the items</p>
                  <Badge variant="secondary">{pointsRemaining} points left</Badge>
                </div>
                <div className="space-y-2">
                  {items.map((item) => (
                    <Card key={item.item_id} className="p-4">
                      <div className="flex items-center gap-4">
                        <Input
                          type="number"
                          min={0}
                          value={pointAllocations[item.item_id] || 0}
                          onChange={(e) => allocatePoints(item.item_id, e.target.value)}
                          className="w-20"
                        />
                        <div className="flex-1">
                          <h4 className="font-medium">{item.title}</h4>
                          {item.description && <p className="text-sm text-gray-500">{item.description}</p>}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
                <Button onClick={() => submitVotes()} disabled={submitting || pointsRemaining === (space.max_points || 100)} className="w-full">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Submit Votes
                </Button>
              </div>
            )}

            {/* Suggest Item Button */}
            {space.allow_suggestions && (
              <div className="mt-8 pt-6 border-t text-center">
                <p className="text-sm text-gray-500 mb-2">Missing something?</p>
                <Button variant="outline" onClick={() => setShowSuggestModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Suggest an Item
                </Button>
              </div>
            )}
          </>
        )}
        </div>
      </div>

      {/* Suggestion Modal */}
      <Dialog open={showSuggestModal} onOpenChange={setShowSuggestModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suggest an Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium">Title *</label>
              <Input
                placeholder="What should be added?"
                value={suggestionTitle}
                onChange={(e) => setSuggestionTitle(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                placeholder="Optional details"
                value={suggestionDescription}
                onChange={(e) => setSuggestionDescription(e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowSuggestModal(false)}>Cancel</Button>
            <Button onClick={handleSuggest} disabled={suggesting}>
              {suggesting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StackRankingPublicVote;
